import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { useRaceStore } from '../../stores/raceStore';
import { useTranslation } from '../../utils/i18n';
import { format } from 'date-fns';
import { AnnotationToggles } from '../ui/ChartAnnotationsMenu';
interface CourseElevationProfileProps {
  onPointClick?: (km: number) => void;
  onHover?: (distKm: number | null) => void;
  annotations: AnnotationToggles;
}
export function CourseElevationProfile({
  onPointClick,
  onHover,
  annotations
}: CourseElevationProfileProps) {
  const {
    t
  } = useTranslation();
  const {
    gpxData,
    segments,
    pacingResults,
    settings
  } = useRaceStore();
  const step = useMemo(() => gpxData ? Math.max(1, Math.floor(gpxData.points.length / 500)) : 1, [gpxData]);
  const data = useMemo(() => {
    if (!gpxData || gpxData.points.length === 0) return [];
    return gpxData.points.filter((_, i) => i % step === 0 || i === gpxData.points.length - 1).map(p => ({
      dist: parseFloat((p.distFromStart || 0).toFixed(2)),
      ele: Math.round(p.ele)
    }));
  }, [gpxData, step]);
  const maxDist = gpxData?.totalDistance || 0;
  const checkpoints = useMemo(() => {
    if (!gpxData) return [];
    let cumulativeGain = 0;
    const startTime = new Date(settings.startTime);
    const points = segments.map((seg, idx) => {
      const result = pacingResults.find(r => r.segmentId === seg.id);
      const pt = gpxData.points[seg.endPointIndex];
      const dist = pt.distFromStart || 0;
      cumulativeGain += seg.elevationGain;
      const prevResult = idx > 0 ? pacingResults.find(r => r.segmentId === segments[idx - 1].id) : null;
      const prevStopTime = prevResult?.stopTime || 0;
      const segmentElapsed = (result?.duration || 0) + prevStopTime;
      return {
        dist,
        cumulativeGain,
        elapsedTime: result?.accumulatedTime || 0,
        segmentElapsed,
        segmentDist: seg.distance,
        segmentGain: seg.elevationGain,
        segmentLoss: seg.elevationLoss,
        name: seg.name,
        eta: result?.eta || null,
        stopTime: seg.stopTime || 0,
        pace: result?.pace || 0
      };
    });
    return [{
      dist: 0,
      cumulativeGain: 0,
      elapsedTime: 0,
      eta: startTime,
      stopTime: 0,
      pace: 0
    }, ...points];
  }, [segments, pacingResults, gpxData, settings.startTime]);
  const hasPacing = pacingResults.length > 0;
  const paceValues = checkpoints.slice(1).map(cp => cp.pace || 0).filter(p => p > 0);
  const minPace = paceValues.length > 0 ? Math.min(...paceValues) : 5;
  const maxPace = paceValues.length > 0 ? Math.max(...paceValues) : 15;
  const paceRange = Math.max(maxPace - minPace, 2);
  const avgPace = useMemo(() => {
    if (!hasPacing || pacingResults.length === 0) return 0;
    const totalRunTime = pacingResults.reduce((sum, r) => sum + r.duration, 0);
    const totalDist = segments.reduce((sum, s) => sum + s.distance, 0);
    return totalDist > 0 ? totalRunTime / totalDist : 0;
  }, [pacingResults, segments, hasPacing]);
  // Higher pace (slower) → higher on screen (lower topPct), lower pace (faster) → lower on screen (higher topPct)
  const paceToTopPct = (pace: number) => {
    const paceNorm = paceRange > 0 ? (pace - minPace) / paceRange : 0.5;
    return 25 + (1 - paceNorm) * 30;
  };
  const formatDuration = (mins: number) => {
    const h = Math.floor(mins / 60);
    const m = Math.round(mins % 60);
    return `${h}h${m.toString().padStart(2, '0')}m`;
  };
  const formatPace = (minPerKm: number) => {
    const m = Math.floor(minPerKm);
    const s = Math.round((minPerKm - m) * 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };
  const handleMouseMove = useCallback((e: any) => {
    if (e && e.activeLabel != null && onHover) onHover(Number(e.activeLabel));
  }, [onHover]);
  const handleMouseLeave = useCallback(() => {
    if (onHover) onHover(null);
  }, [onHover]);
  if (!gpxData || data.length === 0) return null;
  const hasTopAnnotations = annotations.arrivalTimes || annotations.timeDistLabels;
  const topMargin = hasTopAnnotations ? annotations.arrivalTimes ? 55 : 40 : 15;
  // Responsive container width for dynamic thresholds
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(800);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    ro.observe(el);
    setContainerWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);
  // Pixel-based thresholds converted to percentages
  const pctFor = (px: number) => px / containerWidth * 100;
  const minBottomLabelPct = pctFor(55); // ~55px per bottom label
  const minElevPillPct = pctFor(70); // ~70px for elevation pill
  const minBracketPct = pctFor(38); // ~38px for time/dist bracket
  const minArrivalPct = pctFor(52); // ~52px for arrival time labels
  const minPaceLabelPct = pctFor(45); // ~45px for pace text labels
  const isMobile = containerWidth < 500;
  const minStopBadgePx = 40;
  return <div ref={containerRef} className="relative w-full h-full select-none" data-id="element-106">
      <ResponsiveContainer width="100%" height="100%" data-id="element-107">
        <AreaChart data={data} margin={{
        top: topMargin,
        right: 8,
        left: 8,
        bottom: 60
      }} onClick={e => {
        if (e && e.activeLabel && onPointClick) onPointClick(Number(e.activeLabel));
      }} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} style={{
        cursor: onPointClick ? 'crosshair' : undefined
      }} data-id="element-108">
          <defs data-id="element-109">
            <linearGradient id="courseEleGradient" x1="0" y1="0" x2="0" y2="1" data-id="element-110">
              <stop offset="0%" stopColor="#0666EB" stopOpacity={0.3} data-id="element-111" />
              <stop offset="100%" stopColor="#0666EB" stopOpacity={0.03} data-id="element-112" />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E8E8ED" data-id="element-113" />
          <XAxis dataKey="dist" type="number" domain={[0, maxDist]} hide data-id="element-114" />
          <YAxis domain={['dataMin - 50', 'dataMax + 100']} hide data-id="element-115" />
          <Tooltip contentStyle={{
          borderRadius: '12px',
          border: '1px solid #E8E8ED',
          backgroundColor: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(8px)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          padding: '8px 12px'
        }} labelStyle={{
          color: '#9B9BA7',
          fontSize: 11,
          fontWeight: 600
        }} itemStyle={{
          color: '#2D2D2D',
          fontSize: 13,
          fontWeight: 700
        }} formatter={(value: number) => [`${value} m`, 'Elevation']} labelFormatter={value => `${Number(value).toFixed(1)} km`} data-id="element-116" />
          <Area type="monotone" dataKey="ele" stroke="#2D2D2D" strokeWidth={1.5} fill="url(#courseEleGradient)" isAnimationActive={false} activeDot={{
          r: 5,
          fill: '#FC4C02',
          stroke: '#fff',
          strokeWidth: 2
        }} data-id="element-117" />
          {checkpoints.map((cp, idx) => <ReferenceLine key={idx} x={cp.dist} stroke="#2D2D2D" strokeWidth={1} strokeDasharray="2 2" data-id="element-118" />)}
        </AreaChart>
      </ResponsiveContainer>

      <div className="absolute inset-0 pointer-events-none" data-id="element-119">
        {/* Arrival Times */}
        {annotations.arrivalTimes && hasPacing && (() => {
        let lastShownPct = -Infinity;
        return checkpoints.map((cp, idx) => {
          const eta = cp.eta;
          if (!eta) return null;
          const leftPct = cp.dist / maxDist * 100;
          const isFirst = idx === 0;
          const isLast = idx === checkpoints.length - 1;
          // Skip if too close to last shown (but always show first and last)
          if (!isFirst && !isLast && leftPct - lastShownPct < minArrivalPct) return null;
          lastShownPct = leftPct;
          const translateX = isFirst ? '0%' : isLast ? '-100%' : '-50%';
          return <div key={`eta-${idx}`} className="absolute top-0 flex flex-col items-center" style={{
            left: `${leftPct}%`,
            transform: `translateX(${translateX})`
          }} data-id="element-120">
                  <span className="text-[10px] font-bold text-[#2D2D2D] whitespace-nowrap" data-id="element-121">
                    {format(eta, 'HH:mm')}
                  </span>
                </div>;
        });
      })()}
        {/* Time & Distance Bracket Labels */}
        {annotations.timeDistLabels && checkpoints.slice(1).map((cp, idx) => {
        const prevCp = checkpoints[idx];
        const leftPct = prevCp.dist / maxDist * 100;
        const widthPct = (cp.dist - prevCp.dist) / maxDist * 100;
        if (widthPct < minBracketPct) return null;
        const topOffset = annotations.arrivalTimes ? 14 : 2;
        return <div key={`top-${idx}`} className="absolute border-t border-[#38BDF8] flex flex-col items-center justify-center text-[10px] font-medium leading-tight pt-1" style={{
          left: `${leftPct}%`,
          width: `${widthPct}%`,
          top: `${topOffset}px`,
          height: '30px'
        }} data-id="element-122">
                <div className="absolute top-[-4px] left-0 w-px h-1.5 bg-[#38BDF8]" data-id="element-123" />
                <div className="absolute top-[-4px] right-0 w-px h-1.5 bg-[#38BDF8]" data-id="element-124" />
                {hasPacing && <span className="text-[#FC4C02] font-bold" data-id="element-125">
                    {formatDuration(cp.segmentElapsed)}
                  </span>}
                <span className="text-[#6B6B76]" data-id="element-126">
                  {cp.segmentDist.toFixed(1)} km
                </span>
              </div>;
      })}
        {/* Segment Pace Lines + Labels */}
        {annotations.paceLines && hasPacing && checkpoints.slice(1).map((cp, idx) => {
        const prevCp = checkpoints[idx];
        const leftPct = prevCp.dist / maxDist * 100;
        const widthPct = (cp.dist - prevCp.dist) / maxDist * 100;
        const pace = cp.pace || 0;
        if (pace <= 0 || widthPct < 2) return null;
        const lineTopPct = paceToTopPct(pace);
        return <div key={`pace-${idx}`} className="absolute flex items-center" style={{
          left: `${leftPct}%`,
          width: `${widthPct}%`,
          top: `${lineTopPct}%`
        }} data-id="element-127">
                <div className="w-full h-px bg-[#38BDF8]" data-id="element-128" />
                {widthPct > minPaceLabelPct && <span className="absolute left-1/2 -translate-x-1/2 -translate-y-full text-[9px] font-semibold text-[#38BDF8] bg-white/90 px-1 rounded whitespace-nowrap" data-id="element-129">
                    {formatPace(pace)}/km
                  </span>}
              </div>;
      })}
        {/* Average Pace Line */}
        {annotations.avgPace && hasPacing && avgPace > 0 && <div className="absolute flex items-center" style={{
        left: '0%',
        width: '100%',
        top: `${paceToTopPct(avgPace)}%`
      }} data-id="element-130">
            <div className="w-full h-px border-t border-dashed border-[#FC4C02]" data-id="element-131" />
            <span className="absolute right-1 -translate-y-full text-[9px] font-bold text-[#FC4C02] bg-white/90 px-1.5 py-0.5 rounded whitespace-nowrap" data-id="element-132">
              avg {formatPace(avgPace)}/km
            </span>
          </div>}
        {/* Stop Time Badges */}
        {annotations.stopTimeLabels && hasPacing && (() => {
        let lastShownPct = -Infinity;
        return checkpoints.slice(1).map((cp, idx) => {
          const stopTime = cp.stopTime || 0;
          if (stopTime <= 0) return null;
          const leftPct = cp.dist / maxDist * 100;
          // Skip if too close to last shown badge
          if (leftPct - lastShownPct < pctFor(45)) return null;
          lastShownPct = leftPct;
          const isNearEnd = leftPct > 90;
          return <div key={`stop-${idx}`} className="absolute flex justify-center" style={{
            left: `${leftPct}%`,
            transform: isNearEnd ? 'translateX(-100%)' : 'translateX(-50%)',
            bottom: '92px'
          }} data-id="element-133">
                  <span className="bg-[#FC4C02]/10 text-[#FC4C02] text-[9px] font-bold px-1.5 py-0.5 rounded-full border border-[#FC4C02]/20 whitespace-nowrap" data-id="element-134">
                    {stopTime}min
                  </span>
                </div>;
        });
      })()}
        {/* Elevation Gain/Loss */}
        {annotations.elevationGainLoss && checkpoints.slice(1).map((cp, idx) => {
        const prevCp = checkpoints[idx];
        const leftPct = prevCp.dist / maxDist * 100;
        const widthPct = (cp.dist - prevCp.dist) / maxDist * 100;
        if (widthPct < minElevPillPct) return null;
        const isLast = idx === checkpoints.length - 2;
        return <div key={`stats-${idx}`} className={`absolute bottom-[70px] flex ${isLast ? 'justify-end pr-1' : 'justify-center'}`} style={{
          left: `${leftPct}%`,
          width: `${widthPct}%`
        }} data-id="element-135">
                <div className="bg-white/90 backdrop-blur-sm border border-[#E8E8ED] rounded-full px-2 py-0.5 text-[9px] font-semibold text-[#6B6B76] shadow-sm whitespace-nowrap" data-id="element-136">
                  <span className="text-[#00B843]" data-id="element-137">
                    +{Math.round(cp.segmentGain)}m
                  </span>
                  <span className="mx-1 text-[#E8E8ED]" data-id="element-138">|</span>
                  <span className="text-[#E34402]" data-id="element-139">
                    -{Math.round(cp.segmentLoss)}m
                  </span>
                </div>
              </div>;
      })}
        {/* Bottom Checkpoint Info */}
        {checkpoints.map((cp, idx) => {
        const leftPct = cp.dist / maxDist * 100;
        const isFirst = idx === 0;
        const isLast = idx === checkpoints.length - 1;
        const nextCp = idx < checkpoints.length - 1 ? checkpoints[idx + 1] : null;
        const nextLeftPct = nextCp ? nextCp.dist / maxDist * 100 : 100;
        const isCrowded = !isLast && nextLeftPct - leftPct < minBottomLabelPct;
        const translateX = isFirst ? '0%' : isLast ? '-100%' : '-50%';
        const align = isFirst ? 'items-start text-left' : isLast ? 'items-end text-right' : 'items-center text-center';
        if (isCrowded && !isFirst) return null;
        return <div key={`bottom-${idx}`} className={`absolute bottom-0 flex flex-col ${align} text-[10px] leading-tight whitespace-nowrap`} style={{
          left: `${leftPct}%`,
          transform: `translateX(${translateX})`
        }} data-id="element-140">
              <div className="mb-1.5 w-1.5 h-1.5 rounded-full bg-[#2D2D2D] ring-2 ring-white" data-id="element-141" />
              <span className="font-bold text-[#2D2D2D]" data-id="element-142">
                {cp.dist.toFixed(1)} km
              </span>
              <span className="text-[#9B9BA7]" data-id="element-143">
                +{Math.round(cp.cumulativeGain)} m
              </span>
              {hasPacing && <span className="text-[#9B9BA7]" data-id="element-144">
                  {formatDuration(cp.elapsedTime)}
                </span>}
            </div>;
      })}
      </div>
    </div>;
}